import { watch, ref } from 'vue'
import type { Ref } from 'vue'
import store from '@/store'
import type { Conversation } from '@/types/dashboard'
import { useSLA } from './useSLA'
import { fluxoLabel } from './useHelpers'
import { notifySLA } from '@/services/slaService'

// Persiste durante a sessão para evitar spam
const notified = new Set<string>()

function fmtDur(secs: number): string {
  const s = Math.abs(Math.floor(secs))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h > 0) return `${h}h ${m}min`
  if (m > 0) return `${m}min`
  return `${s}s`
}

// Janela de antecedência para pré-filtro: só avalia conversas com prazo
// nos próximos 10 minutos (ou já violadas). Conversas com folga maior são
// puladas sem custo, reduzindo de ~300 para ~10-20 avaliações por ciclo.
const AT_RISK_WINDOW_SECS = 10 * 60

export function useSLANotifications(conversations: Ref<Conversation[]>) {
  const { getProfileForFluxo } = useSLA()
  const tick = ref(0)
  const timer = setInterval(() => { tick.value++ }, 60_000)

  watch([conversations, tick], () => {
    const token = (store.getters.authUser as Record<string, string> | null)?.access_token
    if (!token) return

    const nowSecs = Math.floor(Date.now() / 1000)

    // Pré-filtro: descarta conversas com prazo distante antes de entrar no loop principal
    const candidates = conversations.value.filter(c => {
      if (c.status !== 'open') return false
      const profile = getProfileForFluxo(c.fluxo_id)
      if (!profile?.resolutionTime) return false
      const deadline = c.created_at + profile.resolutionTime * 60
      const remaining = deadline - nowSecs
      const window = Math.max((profile.notifyBefore ?? 0) * 60, AT_RISK_WINDOW_SECS)
      return remaining <= window
    })

    for (const conv of candidates) {
      const profile = getProfileForFluxo(conv.fluxo_id)!
      const deadline = conv.created_at + profile.resolutionTime * 60
      const remaining = deadline - nowSecs
      const breached = remaining <= 0
      const atRisk = !breached && profile.notifyBefore > 0 && remaining <= profile.notifyBefore * 60

      if (!breached && !atRisk) continue

      const type = breached ? 'breached' : 'atRisk'
      const key = `${conv.id}:res:${type}`
      if (notified.has(key)) continue
      notified.add(key)

      notifySLA({
        type,
        conv_id: conv.id,
        contact_name: conv.contact.name,
        contact_phone: conv.contact.phone || '',
        profile_name: profile.name,
        fluxo_name: fluxoLabel(conv.fluxo_id),
        agent_name: conv.assignee?.name || '',
        time_label: fmtDur(remaining),
      })
    }
  }, { immediate: false })

  return { stop: () => clearInterval(timer) }
}
